import {ActionIcon} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Code} from 'src/design/atoms/Code';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {UnstyledButton} from 'src/design/atoms/UnstyledButton';
import {useClipboard, useDisclosure} from '@mantine/hooks';
import React, {useState} from 'react';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CodeIcon,
  CopyIcon,
  IconSize,
} from 'src/design/atoms/icons/CommonIcons';
import {BADGE_TEMPLATE_TOKENS} from './badgeTemplateTokens';

export interface BadgeTemplateTokenPanelProps {
  defaultOpened?: boolean;
}

export const BadgeTemplateTokenPanel: React.FC<
  BadgeTemplateTokenPanelProps
> = ({defaultOpened = false}) => {
  const [opened, {toggle}] = useDisclosure(defaultOpened);
  const clipboard = useClipboard({timeout: 1500});
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const handleCopy = (token: string) => {
    clipboard.copy(`{{${token}}}`);
    setCopiedToken(token);
  };

  return (
    <Box
      style={{border: '1px solid var(--mantine-color-gray-3)', borderRadius: 4}}
      data-testid="badge-template-token-panel">
      <UnstyledButton
        onClick={toggle}
        p="sm"
        w="100%"
        aria-expanded={opened}
        data-testid="badge-template-token-panel-toggle">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <CodeIcon size={IconSize.sm} />
            <Text size="sm" fw={600}>
              Token Reference
            </Text>
            <Text size="xs" c="dimmed">
              ({BADGE_TEMPLATE_TOKENS.length} tokens)
            </Text>
          </Group>
          {opened ? (
            <ChevronDownIcon size={IconSize.sm} />
          ) : (
            <ChevronRightIcon size={IconSize.sm} />
          )}
        </Group>
      </UnstyledButton>

      {opened && (
        <Stack gap={0} px="sm" pb="sm" data-testid="badge-template-token-panel-list">
          {BADGE_TEMPLATE_TOKENS.map(({token, aliases, description}) => (
            <Group
              key={token}
              justify="space-between"
              wrap="nowrap"
              align="flex-start"
              py={6}
              style={{borderTop: '1px solid var(--mantine-color-gray-1)'}}>
              <Box style={{minWidth: 0}}>
                <Group gap={6} wrap="wrap">
                  <Code>{`{{${token}}}`}</Code>
                  {aliases?.map(alias => (
                    <Code key={alias} c="dimmed">
                      {`{{${alias}}}`}
                    </Code>
                  ))}
                </Group>
                <Text size="xs" c="dimmed" mt={2}>
                  {description}
                </Text>
              </Box>
              <Tooltip
                label={copiedToken === token && clipboard.copied ? 'Copied!' : `Copy {{${token}}}`}
                withArrow>
                <ActionIcon
                  variant="subtle"
                  color={copiedToken === token && clipboard.copied ? 'green' : 'gray'}
                  size="sm"
                  aria-label={`Copy {{${token}}} token`}
                  onClick={() => handleCopy(token)}>
                  {copiedToken === token && clipboard.copied ? (
                    <CheckIcon size={IconSize.sm} />
                  ) : (
                    <CopyIcon size={IconSize.sm} />
                  )}
                </ActionIcon>
              </Tooltip>
            </Group>
          ))}
        </Stack>
      )}
    </Box>
  );
};
